import { FC } from 'react'
import { FormField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'

import {
  CreateNotificationContactDetailsDto,
  NotificationContactDetailsDto,
  UpdateNotificationContactDetailsDto,
} from '../../../api/Notifications/Types/NotificationContactDetailsDto'

type ContactDetailsProps = OuterProps & FormikProps<NotificationContactDetailsValues>

const ContactDetailsFormUI: FC<ContactDetailsProps> = (props) => {
  const {
    values,
    errors,
    tickmillCompanies,
    handleSubmit,
    isSubmitting,
    isValid,
    setFieldValue,
    onClose,
  } = props
  const { isNew } = values

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add Contact Details' : 'Update Contact Details'}</DialogTitle>
        </DialogHeader>
        <Form onSubmit={handleSubmit}>
          <div className='flex flex-col gap-4'>
            <FormSelectField
              name='tickmillCompanyId'
              label='Entity'
              options={tickmillCompanies}
              value={values.tickmillCompanyId}
              showSearch
              disabled={!isNew}
              error={errors.tickmillCompanyId}
              onChange={(value) =>
                setFieldValue('tickmillCompanyId', value ? Number(value) : undefined)
              }
            />
            <FormField
              name='supportPhoneNumber'
              label='Phone Number'
              error={errors.supportPhoneNumber}
            />
            <FormField name='supportEmail' label='Email' error={errors.supportEmail} />
          </div>
          <DialogFooter>
            <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
              Cancel
            </Button>
            <Button disabled={isSubmitting || !isValid} type='submit'>
              Save
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

const initialState = {
  tickmillCompany: { id: 0, name: 'None' },
  supportPhoneNumber: '',
  supportEmail: '',
}

const dataToSubmit =
  (values: NotificationContactDetailsValues) =>
  (
    props: OuterProps
  ): CreateNotificationContactDetailsDto | UpdateNotificationContactDetailsDto => {
    const { data } = props
    const { isNew, supportPhoneNumber, supportEmail, tickmillCompanyId } = values || {}
    const tickmillCompany = props?.tickmillCompanies?.find(
      (x) => x.id.toString() === tickmillCompanyId?.toString()
    )

    const formData = {
      supportPhoneNumber:
        supportPhoneNumber || data?.supportPhoneNumber || initialState.supportPhoneNumber,
      supportEmail: supportEmail || data?.supportEmail || initialState.supportEmail,
    }

    if (isNew) {
      return formData
    }

    return {
      ...formData,
      tickmillCompanyId:
        tickmillCompany?.id || data?.tickmillCompany?.id || initialState.tickmillCompany?.id,
    }
  }

export interface OuterProps {
  isOpen?: boolean
  onClose(): void
  tickmillCompanies: TickmillCompanyDto[]
  data?: NotificationContactDetailsDto | undefined
  onSubmit(
    values: CreateNotificationContactDetailsDto | UpdateNotificationContactDetailsDto
  ): Promise<void>
}

type NotificationContactDetailsValues = Partial<
  Omit<NotificationContactDetailsDto, 'tickmillCompany'>
> & {
  tickmillCompanyId: number | undefined
  isNew: boolean
}

export const isContactDetailsNew = (
  values: CreateNotificationContactDetailsDto | UpdateNotificationContactDetailsDto
): values is CreateNotificationContactDetailsDto => {
  return !('tickmillCompanyId' in values)
}

export const isContactDetailsUpdate = (
  values: CreateNotificationContactDetailsDto | UpdateNotificationContactDetailsDto
): values is UpdateNotificationContactDetailsDto => {
  return 'tickmillCompanyId' in values
}

export const ContactDetailsForm = withFormik<OuterProps, NotificationContactDetailsValues>({
  mapPropsToValues: (props) => {
    return {
      ...initialState,
      tickmillCompanyId: props.data?.tickmillCompany?.id || undefined,
      supportPhoneNumber: props.data?.supportPhoneNumber || '',
      supportEmail: props.data?.supportEmail || '',
      isNew: !props.data?.tickmillCompany?.id,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      await props.onSubmit(dataToSubmit(values)(props))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<NotificationContactDetailsValues> = {}

    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'Required'
    }

    if (!values.supportPhoneNumber) {
      errors.supportPhoneNumber = 'Required'
    }

    if (!values.supportEmail) {
      errors.supportEmail = 'Required'
    }

    return errors
  },
  isInitialValid: false,
  enableReinitialize: true,
})(ContactDetailsFormUI)
